<template>
  <div class="tag-select-container">
    <div class="select-container">
      <el-select v-model="matchType" class="match-type">
        <el-option
          v-for="(val, key) in MATCH_TYPE"
          :key="key"
          :label="$t(`${i18nPrefix}.tagMatchType.${key}`)"
          :value="val"
        ></el-option>
      </el-select>
      <span class="tag-label" @click="handleLabelClick">{{ $t(`${i18nPrefix}.label.selectedTag`, {count: selectedCount || 0}) }}</span>
    </div>
    <DialogWrapper
      close-on-cancel
      class="tag-selector"
      :title="$t(`${i18nPrefix}.title.tag`)"
      :show.sync="show"
      :loading="loading"
      @save="handleSave"
      @closed="handleClosed"
    >
      <div class="tag-container">
        <div class="count-container">
          {{ `${$t(`${i18nPrefix}.label.searchResult`)}: ${formatBigNumber(totalCount)}` }}
          {{ `${$t(`${i18nPrefix}.label.selected`)}: ${formatBigNumber(dialogSelectedCount)}` }}
          <el-button class="clear-btn" @click="handleClickSelected">{{ $t(`${i18nPrefix}.btn.clear`) }}</el-button>
        </div>
        <div class="selected-tag">
          <div v-for="tag in selectedTagList" v-auto-tooltip.always="getSelectedTagTip(tag)" class="tag" :key="tag.id">
            <span class="label">{{ tag.i18nName }}</span>
            <div class="cancel-btn" @click="cancelSelected(tag)">
              <i class="btn-icon el-icon-close"></i>
            </div>
          </div>
        </div>
        <div class="search-container">
          <el-input
            v-model="keywords"
            class="keywords-input"
            suffix-icon="el-icon-search"
            clearable
            :placeholder="$t('common.search')"
            @input="handleSearchTagByKeywords"
          ></el-input>
        </div>
        <div class="list-head">
          <el-checkbox v-model="isSelectAll" class="head-checkbox" :indeterminate="indeterminate"></el-checkbox>
          <el-select v-model="dialogMatchType" class="match-type">
            <el-option
              v-for="(val, key) in MATCH_TYPE"
              :key="key"
              :label="$t(`${i18nPrefix}.tagMatchType.${key}`)"
              :value="val"
            ></el-option>
          </el-select>
        </div>
        <div v-infinite-scroll="infiniteLoadOthersTagList" infinite-scroll-immediate="false" infinite-scroll-distance="10" class="others-tags">
          <div
            v-for="(tag, tagIndex) in othersTagType"
            v-show="tag.show"
            :key="tagIndex"
            class="list-item tag-item"
            :class="tag.selected && 'selected'"
          >
            <el-checkbox v-model="tag.selected" class="tag-checkbox">
              <span class="tag-name">{{ tag.i18nName }}</span>
            </el-checkbox>
          </div>
        </div>
        <el-collapse v-if="tagTypeList.length" v-model="tagCollapseActiveList" class="tag-collapse" @change="handleCollapseActiveChange">
          <el-collapse-item
            v-for="(item, index) in tagTypeList"
            class="tag-collapse-item"
            :key="index"
            :title="item.label"
            :name="item.type"
          >
            <div :ref="`tagLitRef_${item.type}`" :id="`${item.type}`" v-infinite-scroll="infiniteLoadTagList" infinite-scroll-immediate="false" infinite-scroll-distance="10" class="tag-list">
              <div
                v-for="(tag, tagIndex) in item.tagList"
                v-show="tag.show"
                :key="tagIndex"
                class="list-item tag-item"
                :class="tag.selected && 'selected'"
              >
                <el-checkbox v-model="tag.selected" class="tag-checkbox">
                  <span class="tag-name">{{ tag.i18nName }}</span>
                </el-checkbox>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { formatBigNumber } from '@/components/filters'
import { addI18nProperty } from '@/i18n'
import { TAG_TYPE_LIST } from '@/constant/crm'

const MATCH_TYPE = {
  ALL: 'ALL',
  ANY: 'ANY'
}

export default {
  name: 'tag-select',
  components: {
    DialogWrapper
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      required: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    categoryList: {
      type: Array,
      default: () => ['manual', 'smart']
    }
  },
  data () {
    const i18nPrefix = 'admin.application.applicantList'
    return {
      i18nPrefix,
      formatBigNumber,
      MATCH_TYPE,
      loading: true,
      show: false,
      keywords: null,
      dialogMatchType: MATCH_TYPE.ALL,
      tagLoading: true,
      tagListLoader: null,
      totalTagList: [],
      searchedTagList: [],
      tagList: [],
      totalCount: 0,
      tagCollapseActiveList: [],
      tagCollapsePageMap: {},
      tagCollapseScrollName: null,
      tagTypes: ['Opened', 'Shared', 'Applied for', 'Others']
    }
  },
  computed: {
    hasCRMTagPermission () {
      return this.hasAnyPermission('PREMIUM_TAG_MANAGEMENT')
    },
    matchType: {
      get () {
        if (!this.value) {
          return MATCH_TYPE.ALL
        }
        return this.value.isMatchAll ? MATCH_TYPE.ALL : MATCH_TYPE.ANY
      },
      set (val) {
        if (!this.value) {
          this.$emit('change', { selected: [], isMatchAll: val === MATCH_TYPE.ALL })
        } else {
          this.value.isMatchAll = val === MATCH_TYPE.ALL
        }
      }
    },
    selectedCount () {
      return this.value?.selected?.length
    },
    dialogSelectedCount () {
      return _.filter(this.totalTagList, 'selected').length
    },
    isSelectAll: {
      get () {
        if (this.totalCount < 1) {
          return false
        }
        let tagList = this.totalTagList
        if (this.keywords) {
          tagList = this.searchedTagList
        }
        const selectCount = _.filter(tagList, 'selected').length
        return selectCount >= this.totalCount
      },
      set (val) {
        let tagList = this.totalTagList
        if (this.keywords) {
          tagList = this.searchedTagList
        }
        _.forEach(tagList, tag => {
          tag.selected = val
        })
      }
    },
    indeterminate () {
      let tagList = this.totalTagList
      if (this.keywords) {
        tagList = this.searchedTagList
      }
      const selectCount = _.filter(tagList, 'selected').length
      return selectCount > 0 && selectCount < tagList.length
    },
    othersTagType () {
      return _.chain(this.tagList).filter(tag => tag.type === 'OTHERS').value()
    },
    searchedTagListMap () {
      return _.chain(this.searchedTagList).groupBy(tag => tag.type).value()
    },
    tagTypeList () {
      const vm = this
      return _.chain(this.tagList).filter(tag => tag.type !== 'OTHERS').groupBy(tag => tag.type).map((val, key) => {
        return {
          get label () {
            return vm.$t(`admin.crm.tagTypes.${key}`)
          },
          type: key,
          order: _.indexOf(this.tagTypes, key),
          tagList: val
        }
      }).orderBy('order').value()
    },
    selectedTagList () {
      return _.filter(this.totalTagList, tag => tag.selected)
    }
  },
  created () {
    this.tagListLoader = this.loadTagList()
  },
  methods: {
    ...mapActions('crm', ['searchTag']),
    async refreshDialog () {
      this.keywords = null
      this.restTagCollapsePageMap()
      await this.handleSearch()
      this.dialogMatchType = this.matchType
      const selectedId = _.map(this.value?.selected, 'id')
      _.forEach(this.totalTagList, tag => {
        tag.selected = selectedId.includes(tag.id)
      })
    },
    restTagCollapsePageMap (isInit) {
      if (isInit) {
        _.forEach(this.tagTypes, name => {
          this.tagCollapsePageMap[name] = {
            current: 1,
            size: 20,
            get startIndex () {
              return (this.current - 1) * this.size
            },
            get endIndex () {
              return this.current * this.size
            }
          }
        })
      } else {
        _.forEach(this.tagTypes, name => {
          this.tagCollapsePageMap[name].current = 1
        })
      }
    },
    handleCollapseActiveChange () {
      _.forEach(this.tagCollapseActiveList, name => {
        const tagListRef = this.$refs[`tagLitRef_${name}`]
        if (tagListRef) {
          tagListRef[0].addEventListener('scroll', this.handleScroll)
        }
      })
    },
    handleScroll (event) {
      this.tagCollapseScrollName = event.target.id
    },
    infiniteLoadTagList () {
      this.tagCollapsePageMap[this.tagCollapseScrollName].current += 1
      this.setPagedTagList()
    },
    infiniteLoadOthersTagList () {
      this.tagCollapseScrollName = 'OTHERS'
      this.tagCollapsePageMap.OTHERS.current += 1
      this.setPagedTagList()
    },
    async loadTagList () {
      if (!this.hasCRMTagPermission) {
        return
      }
      this.loading = true
      try {
        const params = {
          is_company_tag: 1,
          tag_category: this.categoryList.join(',')
        }
        const resp = await this.searchTag(params)
        if (resp) {
          const tagList = _.map(resp, tag => {
            const tagInfo = tag
            tag.type = _.upperCase(tag.type)
            tagInfo.show = true
            tagInfo.selected = false
            addI18nProperty(tagInfo, 'i18nName', 'cn_name', 'name')
            return tagInfo
          })
          this.totalTagList = _.orderBy(tagList, [(tag) => tag.name.toUpperCase()])
          this.totalCount = this.totalTagList.length
          this.tagTypes = _.map(TAG_TYPE_LIST, tag => _.toUpper(tag))
          this.restTagCollapsePageMap(true)
        }
      } finally {
        this.loading = false
      }
    },
    async setPagedTagList () {
      this.loading = true
      let page = {
        current: 1,
        size: 20,
        startIndex: 0,
        endIndex: 20
      }
      if (this.tagCollapseScrollName) {
        page = this.tagCollapsePageMap[this.tagCollapseScrollName]
      }
      let searchedTagList = []
      if (this.tagCollapseScrollName) {
        searchedTagList = _.concat(searchedTagList, _.get(this.searchedTagListMap, this.tagCollapseScrollName, []))
      } else if (page.current === 1) {
        _.forEach(this.searchedTagListMap, (val, key) => {
          if (val.length) {
            searchedTagList = _.concat(searchedTagList, _.slice(val, page.startIndex, page.endIndex))
          }
        })
      } else {
        searchedTagList = this.searchedTagList
      }
      try {
        const pageData = page.current === 1 ? searchedTagList : _.slice(searchedTagList, page.startIndex, page.endIndex)
        if (page.current === 1) {
          this.tagList = pageData
        } else {
          this.tagList.push(...pageData)
        }
        await this.$nextTick()
      } finally {
        this.loading = false
      }
    },
    async handleLabelClick () {
      this.show = true
      this.loading = true
      await this.refreshDialog()
      this.$nextTick(() => {
        this.loading = false
      })
    },
    handleSearchTagByKeywords () {
      if (!this.searchKeywordsDebounce) {
        this.searchKeywordsDebounce = _.debounce(this.handleSearch, 500)
      }
      this.tagCollapseScrollName = null
      this.searchKeywordsDebounce()
    },
    async handleSearch () {
      await this.tagListLoader
      this.restTagCollapsePageMap()
      if (this.keywords) {
        this.searchedTagList = _.filter(this.totalTagList, tag => tag.i18nName.toUpperCase().includes(this.keywords.toUpperCase()))
        this.tagCollapseActiveList = _.chain(this.searchedTagList).map(tag => tag.type).uniq().value()
      } else {
        this.searchedTagList = this.totalTagList
      }
      this.totalCount = this.searchedTagList.length
      this.setPagedTagList()
    },
    handleClosed () {
      this.tagCollapseActiveList = []
      this.tagCollapseScrollName = null
    },
    handleSave () {
      const value = this.value || {}
      const selected = _.map(this.selectAll ? this.totalTagList : this.selectedTagList, tag => {
        return { id: tag.id, name: tag.name, cn_name: tag.cn_name }
      })
      value.isMatchAll = this.dialogMatchType === MATCH_TYPE.ALL
      value.selected = selected
      if (!this.value) {
        this.$emit('change', value)
      }
      this.tagCollapseActiveList = []
      this.show = false
    },
    getSelectedTagTip (tag) {
      if (tag.type !== 'OTHERS') {
        return `${this.$t(`admin.crm.tagTypes.${tag.type}`)}: ${tag.i18nName}`
      } else {
        return tag.i18nName
      }
    },
    cancelSelected (tag) {
      tag.selected = false
    },
    handleClickSelected () {
      _.forEach(this.totalTagList, tag => {
        tag.selected = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.tag-select-container {
  .select-container {
    // display: flex;
    .match-type {
      width: 45%;
      vertical-align: middle;

      &:hover {
        margin-right: -1px;
        position: relative;
        z-index: 1;

        + .tag-label {
          margin-left: 0;
        }
      }

      ::v-deep {
        .el-input__inner {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        .el-input__suffix {
          right: 0;
        }
      }
    }

    .tag-label {
      display: inline-block;
      width: 55%;
      height: 32px;
      line-height: 32px;
      margin-left: -1px;
      padding: 0 4px;
      border: 1px solid #DCDFE6;
      border-radius: 4px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      background: #ffffff;
      color: #232323;
      text-align: center;
      vertical-align: middle;
      cursor: pointer;
    }
  }

  .tag-selector {
    .tag-container {
      margin: 30px auto 0;

      .search-container {
        margin-top: 15px;

        .keywords-input {
          line-height: 32px;
        }
      }

      .list-head {
        display: flex;
        margin-top: 15px;
        align-items: center;

        .head-checkbox {
          margin-left: 5px;
        }

        .match-type {
          margin-left: 15px;
        }
      }

      .others-tags {
        max-height: 140px;
        margin-top: 15px;
        overflow: auto;
        position: relative;

        .list-item {
          padding: 0 5px;

          &.selected {
            background: #edfaff;
          }

          &:hover {
            background: #f5f6f5;
          }

          .tag-checkbox {
            width: 100%;
            padding: 8px 0;
          }
        }
      }

      .tag-collapse {
        margin-top: 10px;
        .tag-collapse-item {
          .tag-list {
            max-height: 220px;
            margin-top: 15px;
            overflow: auto;
            position: relative;

            .list-item {
              padding: 0 5px;

              &.selected {
                background: #edfaff;
              }

              &:hover {
                background: #f5f6f5;
              }

              .tag-checkbox {
                width: 100%;
                padding: 8px 0;
              }
            }
          }
        }
      }

      .count-container {
        margin-bottom: 14px;
        display: flex;
        justify-content: space-between;

        .clear-btn {
          padding: 0 20px;
          line-height: 30px;
        }
      }

      .tag-list-loading {
        position: absolute;
      }
    }

    .selected-tag {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      max-height: 92px;
      overflow: auto;
      margin-bottom: -14px;

      .tag {
        margin-right: 14px;
        margin-bottom: 14px;
        padding: 8px 15px;
        border-radius: 18px;
        border: 1px solid #dddedf;
        line-height: 10px;

        .label {
          vertical-align: middle;
          line-height: 1;
        }

        .cancel-btn {
          display: inline-block;
          vertical-align: middle;
          cursor: pointer;

          .btn-icon {
            font-size: 12px;
            margin-left: 10px;
          }
        }
      }
    }
  }
}
</style>
